import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import {
  NumerolojiProductEditor,
  type ProductDraft,
} from "@/components/admin/NumerolojiProductEditor";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { emptyProductDetailMeta, splitProductTagsAndMeta } from "@/lib/shop-product-meta";

import { Bell } from "lucide-react";

type CatalogProduct = {
  id: string;
  sku: string;
  name: string;
  description: string;
  category: string;
  priceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  tags: string[];
  imageUrl?: string;
  imageUrls?: string[];
};

type CatalogResponse = {
  ok: true;
  packages: unknown[];
  products: CatalogProduct[];
  templates: unknown[];
  settings: unknown;
};

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await adminFetch("/api/admin/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function saveProduct(payload: Partial<CatalogProduct> & { sku: string; name: string }) {
  const res = await adminFetch("/api/admin/numeroloji/products", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ürün kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogProduct };
}

function emptyDraft(): ProductDraft {
  return {
    sku: "",
    name: "",
    description: "",
    category: "other",
    priceTry: 0,
    discountedPriceTry: undefined,
    active: true,
    tagsText: "",
    imageUrl: "",
    imageUrls: [],
    detailMeta: emptyProductDetailMeta(),
  };
}

export default function AdminNumerolojiProduct() {
  const params = useParams();
  const productId = params.id ? String(params.id).trim() : "";
  const isNew = !productId;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const catalogQuery = useQuery({
    queryKey: ["admin-numeroloji-catalog"],
    queryFn: fetchCatalog,
    enabled: authenticated,
    staleTime: 30_000,
  });

  const products = useMemo(() => catalogQuery.data?.products ?? [], [catalogQuery.data]);

  const [draft, setDraft] = useState<ProductDraft>(() => emptyDraft());
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (!authenticated) return;

    if (isNew) {
      if (!seeded) {
        setDraft(emptyDraft());
        setSeeded(true);
      }
      return;
    }

    if (!catalogQuery.data || seeded) return;

    const item = products.find((p) => p.id === productId);
    if (!item) {
      setSeeded(true);
      return;
    }

    const { cleanTags, meta } = splitProductTagsAndMeta(item.tags ?? []);

    setDraft({
      id: item.id,
      sku: item.sku,
      name: item.name,
      description: item.description,
      category: item.category,
      priceTry: item.priceTry,
      discountedPriceTry: item.discountedPriceTry,
      active: item.active,
      tagsText: cleanTags.join(", "),
      imageUrl: item.imageUrl ?? "",
      imageUrls: Array.isArray(item.imageUrls) ? item.imageUrls : item.imageUrl ? [item.imageUrl] : [],
      detailMeta: meta,
    });
    setSeeded(true);
  }, [authenticated, catalogQuery.data, isNew, productId, products, seeded]);

  const mutation = useMutation({
    mutationFn: (payload: any) => saveProduct(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
      navigate("/admin/numeroloji/urun", { replace: true });
    },
  });

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  if (!isNew && catalogQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!isNew && catalogQuery.isError) {
    return <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>;
  }

  if (!isNew && catalogQuery.data && !products.some((p) => p.id === productId)) {
    return (
      <div className="p-4 sm:p-6">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          Ürün bulunamadı.
        </div>
        <div className="mt-4">
          <Button className="rounded-full" onClick={() => navigate("/admin/numeroloji/urun", { replace: true })}>
            Ürün listesine dön
          </Button>
        </div>
      </div>
    );
  }

  const headerTitle = isNew ? "Yeni Ürün" : "Ürün Düzenle";

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
              mode="links"
              isDark={isDark}
              onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
            />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                {headerTitle}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <NumerolojiProductEditor
            draft={draft}
            setDraft={setDraft}
            isSaving={mutation.isPending}
            onClose={() => navigate("/admin/numeroloji/urun", { replace: true })}
            onSave={(payload) => mutation.mutate(payload)}
          />

          {mutation.isError ? (
            <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {(mutation.error as Error).message}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
